import { Button, Checkbox, FormControlLabel, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Box } from '@mui/system';
import { SelectField, StyledTextField } from '@resurs-internal/mui';
import { type MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { encryptInitPayloadService } from '../../api/encryption';
import { ConsumerLoanTargetPath } from '../../domain/types';
import { ConsumerLoanInitPayload } from '../../domain/types/ConsumerLoanInitPayload';
import { ConsumerLoanParameter } from '../../domain/types/ConsumerLoanParameter';
import { getPath } from '../../helpers/url';
import { useLocalePath } from '../../hooks/useLocalePath';
import { useLocalStorageValue } from '../../hooks/useLocalStorage';
import useSettings from '../../hooks/useSettings';
import { MockServiceWorker } from '../../mocks/mockServiceWorker';
import { SelectFieldStyles } from './CalculatorPage.styles';

const ORIGINATING_URL_OPTIONS = [
    {
        id: 'https://www.resursbank.se/',
        value: 'https://www.resursbank.se/',
        label: 'Resursbank SE',
    },
    {
        id: 'https://www.resursbank.no/',
        value: 'https://www.resursbank.no/',
        label: 'Resursbank NO',
    },
    {
        id: 'https://www.resursbank.fi/',
        value: 'https://www.resursbank.fi/',
        label: 'Resursbank FI',
    },
    {
        id: 'https://www.resursbank.dk/',
        value: 'https://www.resursbank.dk/',
        label: 'Resursbank DK',
    },
];

export const CalculatorPage = () => {
    const settings = useSettings();
    const [governmentId, setGovernmentId] = useState<string>();
    const [amount, setAmount] = useState<number>(100000);
    const [tenor, setTenor] = useState<number>(60);
    const [originatingUrl, setOriginatingUrl] = useState<string>(ORIGINATING_URL_OPTIONS[0].id);
    const [useConsolidation, setUseConsolidation] = useState(false);
    const { language, countryCode } = useLocalePath();
    const navigate = useNavigate();
    const isDemoModeEnable = useLocalStorageValue(MockServiceWorker.DEMO_MODE_ACTIVE_STORAGE_KEY, 'false');

    const onInitApplication = () => {
        const initPayload: ConsumerLoanInitPayload = {
            governmentId,
            amount: amount,
            tenor: tenor,
            originatingUrl,
            useConsolidation,
        };

        encryptInitPayloadService(settings.proxyBaseUrl, settings.apiKey, initPayload).then((result) => {
            navigate({
                pathname: getPath(ConsumerLoanTargetPath.init, language, countryCode),
                search: `${ConsumerLoanParameter.initPayload}=${result}`,
            });
        });
    };

    const createOptions = () => {
        const result = [];
        for (let years = 1; years <= 15; years++) {
            result.push({
                id: '' + years * 12,
                value: '' + years * 12,
                label: years + ' years',
            });
        }
        return result;
    };

    return (
        <Box marginBottom={2} marginTop={2}>
            <Box display={'flex'} justifyContent={'space-between'} gap={1} marginBottom={2} marginTop={2}>
                <ToggleButtonGroup
                    color="primary"
                    exclusive
                    value={language + '_' + countryCode}
                    onChange={(_event: MouseEvent<HTMLElement>, value: string) => navigate('/' + value)}>
                    <ToggleButton value="sv_SE">SE</ToggleButton>
                    <ToggleButton value="no_NO">NO</ToggleButton>
                    <ToggleButton value="fi_FI">FI</ToggleButton>
                    <ToggleButton value="da_DK">DK</ToggleButton>
                </ToggleButtonGroup>
                {MockServiceWorker.isMockingAvailable() && (
                    <ToggleButton
                        value={'demo'}
                        onClick={async () => {
                            // Always stop() first, even when entering demo mode -- it's the only thing that
                            // invalidates stale auth/form state (see mockServiceWorker.tsx), and unlike the
                            // other three demo toggles below, this one used to call start() directly, which
                            // skipped that cleanup entirely and could let a fresh demo session inherit a
                            // previous session's token and form data.
                            const wasActive = MockServiceWorker.isActive();
                            MockServiceWorker.stop();
                            if (!wasActive) {
                                // Awaited (matching the other three toggles below) so MSW is actually
                                // registered and intercepting requests before this handler returns -- start()
                                // flips isDemoModeActive() and fires its storage event synchronously, before
                                // that registration completes, and with the demo-token bypass gone, a rapid
                                // navigation into an authenticated page during that gap could send /auth/init
                                // to the real endpoint instead of the mock.
                                await MockServiceWorker.start('mainApplicant');
                            }
                        }}
                        selected={isDemoModeEnable === 'true' && MockServiceWorker.isMainApplicant()}>
                        DEMO MODE
                    </ToggleButton>
                )}
                {MockServiceWorker.isMockingAvailable() && (
                    <ToggleButton
                        value={'demo coapplicant'}
                        onClick={async () => {
                            MockServiceWorker.stop();
                            await MockServiceWorker.start('coApplicant');
                            navigate(
                                getPath(
                                    ConsumerLoanTargetPath.coapplicantInit,
                                    language,
                                    countryCode,
                                    'demoApplication',
                                ),
                            );
                        }}>
                        DEMO MODE CO-APPLICANT
                    </ToggleButton>
                )}
                {MockServiceWorker.isMockingAvailable() && (
                    <ToggleButton
                        value={'draft'}
                        onClick={async () => {
                            MockServiceWorker.stop();
                            await MockServiceWorker.start('draft');
                            navigate(
                                getPath(ConsumerLoanTargetPath.draftInit, language, countryCode, 'demoApplication'),
                            );
                        }}>
                        DEMO MODE DRAFT
                    </ToggleButton>
                )}
                {MockServiceWorker.isMockingAvailable() && (
                    <ToggleButton
                        value={'coApplicantDraft'}
                        onClick={async () => {
                            MockServiceWorker.stop();
                            await MockServiceWorker.start('coApplicantDraft');
                            navigate(
                                getPath(
                                    ConsumerLoanTargetPath.coapplicantDraftInit,
                                    language,
                                    countryCode,
                                    'demoApplication',
                                ),
                            );
                        }}>
                        DEMO MODE CO-APPLICANT DRAFT
                    </ToggleButton>
                )}
            </Box>
            <Box marginBottom={2} marginTop={2}>
                <FormControlLabel
                    control={
                        <Checkbox
                            id="useConsolidation"
                            name="useConsolidation"
                            checked={useConsolidation}
                            onChange={() => setUseConsolidation(!useConsolidation)}
                        />
                    }
                    label={'Use consolidation'}
                />
            </Box>

            <Box marginBottom={2} marginTop={2}>
                <StyledTextField
                    id="governmentId"
                    name="governmentId"
                    value={governmentId ?? ''}
                    onChange={(e) => setGovernmentId(e.target.value)}
                    label="Government Id"
                />
            </Box>

            <Box marginBottom={2} marginTop={2}>
                <StyledTextField
                    id="amount"
                    name="amount"
                    value={amount}
                    onChange={(e) => setAmount(parseInt(e.target.value))}
                    label="Loan amount"
                />
            </Box>

            <Box marginBottom={2} marginTop={2}>
                <SelectField
                    sx={SelectFieldStyles}
                    id="tenor"
                    name="tenor"
                    value={'' + tenor}
                    onChange={(event: { target: { value: string } }) => setTenor(parseInt('' + event.target.value))}
                    label="Tenor"
                    fullWidth
                    variant="outlined"
                    options={createOptions()}></SelectField>
            </Box>

            <Box marginBottom={4} marginTop={4}>
                <SelectField
                    sx={SelectFieldStyles}
                    id="originatingUrl"
                    name="originatingUrl"
                    value={originatingUrl}
                    onChange={(event: { target: { value: string } }) => setOriginatingUrl('' + event.target.value)}
                    label="OriginatingUrl"
                    fullWidth
                    variant="outlined"
                    options={ORIGINATING_URL_OPTIONS}></SelectField>
            </Box>

            <Button onClick={onInitApplication}>Start application</Button>
        </Box>
    );
};
